import {CardContent, Grid} from "@mui/material";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {
	BreadcrumbItem,
	BreadcrumbLink,
	Breadcrumbs,
	EditForm,
	PlainDateTime,
	PlainDateTimePicker,
	TextField,
	ViewHeader,
} from "@variocube/app-ui";
import React, {useState} from "react";
import {Link, Navigate, useNavigate} from "react-router-dom";
import {useBookingShareApi} from "../../../api/bookingShare";
import EmailInput from "../../../controls/EmailInput";
import {useLocalization, useWordingFlavor} from "@smalox/shared";
import {useTenantFeature, useTenantId} from "../../../tenant";
import {UserTimeZoneId} from "../../../utils/date";
import {queryKeys} from "../../../utils/queryKeys";
import {useNumericParam} from "../../../utils/useParam";
import {View} from "../../View";

export function BookingShareCreate() {
	const {t} = useLocalization();
	const wordingFlavor = useWordingFlavor();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const tenantId = useTenantId();
	const bookingId = useNumericParam("bookingId");
	const {createBookingShare} = useBookingShareApi();

	const canShowBookingShares = useTenantFeature("SHOW_BOOKING_SHARES");

	const [name, setName] = useState<string>("");
	const [validUntil, setValidUntil] = useState<PlainDateTime | null>(null);
	const [emails, setEmails] = useState<string[]>([]);

	const {isLoading, mutateAsync: handleSave} = useMutation({
		mutationFn: () =>
			createBookingShare(bookingId, {
				name: name,
				validUntil: validUntil?.toZonedDateTime(UserTimeZoneId)?.toInstant()?.toString(),
				emails: emails,
			}),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: queryKeys.bookingShares.byBooking(tenantId, bookingId),
			});
			await queryClient.invalidateQueries({
				queryKey: queryKeys.bookingShares.count(tenantId, bookingId),
			});
			navigate(`../${bookingId}?tab=bookingShares`);
		},
	});

	if (canShowBookingShares === false) {
		return <Navigate to={".."} />;
	}

	return (
		<View>
			<Breadcrumbs>
				<BreadcrumbLink component={Link} to={"../.."} relative="path">{t(`bookings.title.${wordingFlavor}`)}</BreadcrumbLink>
				<BreadcrumbLink component={Link} to={"..?tab=bookingShares"} relative="path">
					{bookingId}
				</BreadcrumbLink>
				<BreadcrumbItem>{t(`bookings.shares.create.${wordingFlavor}`)}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader title={t(`bookings.shares.create.${wordingFlavor}`)} />
			<EditForm
				loading={isLoading}
				onSave={handleSave}
				labels={t}
			>
				<CardContent>
					<Grid container spacing={2}>
						<Grid item container>
							<Grid item xs={12} sm={6}>
								<TextField
									label={t("bookings.shares.name")}
									value={name}
									onChange={setName}
									fullWidth
									required
									inputProps={{
										maxLength: 128,
									}}
									helperText={t("bookings.shares.maxLengthName")}
								/>
							</Grid>
						</Grid>
						<Grid item container>
							<Grid item xs={12} sm={6}>
								<PlainDateTimePicker
									label={t("bookings.shares.validUntil")}
									value={validUntil}
									onChange={setValidUntil}
									fullWidth
								/>
							</Grid>
						</Grid>
						<Grid item container>
							<Grid item xs={12} sm={6}>
								<EmailInput emails={emails} setEmails={setEmails} />
							</Grid>
						</Grid>
					</Grid>
				</CardContent>
			</EditForm>
		</View>
	);
}
